import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import type {
  AgentEvent,
  Page,
  RcaClient,
  RunStatus,
  Scope,
  SessionView,
  UploadView,
} from "@rca/sdk";
import { RcaApiError } from "@rca/sdk";
import { subscribeToRun } from "@rca/sdk";
import { loadSessionHistory } from "@rca/sdk";
import {
  chatReducer,
  initialChatState,
  type ChatMessage,
  type ChatState,
} from "./reducer.js";
import { BlurFade, BorderBeam, ShinyText } from "../ui/magic.js";

/**
 * チャット画面（W5-2 / FR-W1）。
 *
 * 表示に必要な畳み込みは `chatReducer` に出してあるので、
 * ここは「入力を受けて API を呼び、イベントを reducer に流す」だけにしている。
 */

export interface ChatViewProps {
  readonly client: RcaClient;
  readonly getToken: () => string | null;
  readonly scope: Scope;
  /** テストから購読を差し替えるための穴。 */
  readonly subscribe?: typeof subscribeToRun;
  /**
   * 選んだセッション ID の保持先。既定は `sessionStorage`。
   *
   * タブを切り替えると `ChatView` は unmount されて state が消えるので、
   * ここに置かないと「別ページに行ったらセッションが消える」ことになる。
   * `localStorage` は使わない（タブを閉じても残るため）。
   */
  readonly storage?: Storage;
}

/** 保持キー。値はセッション ID だけで、秘密は含まない。 */
const SESSION_KEY = "rca.chat.session";

/** これらに達していれば、もう続きは来ない。 */
const TERMINAL: readonly RunStatus[] = ["succeeded", "failed", "cancelled", "timeout"];

const RUNNER_TYPES = ["claude-code", "codex"] as const;

const STATUS_TONE: Record<string, string> = {
  succeeded: "ok",
  failed: "danger",
  timeout: "danger",
  cancelled: "warn",
};

export function ChatView({
  client,
  getToken,
  scope,
  subscribe,
  storage,
}: ChatViewProps): React.JSX.Element {
  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const [prompt, setPrompt] = useState("");
  const [session, setSession] = useState<SessionView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connection, setConnection] = useState<"idle" | "connecting" | "open" | "closed">("idle");
  const [picking, setPicking] = useState(false);
  /**
   * 名指しで選んだ Environment。空文字は「自動」（スコープチェーンのまま）。
   *
   * ★ 同じ Runner の Environment を複数持てるので、選べないと
   * どれで走ったのか分からないまま使うことになる。
   */
  const [environmentId, setEnvironmentId] = useState("");
  const [environments, setEnvironments] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    let alive = true;
    client.environments
      .list({ projectId: scope.projectId ?? null })
      .then((page) => {
        if (alive) setEnvironments(page.items.map((doc) => ({ id: doc.id, name: doc.name })));
      })
      // member でも一覧は引けるが、落ちても会話は続けられる（自動になるだけ）。
      .catch(() => {
        if (alive) setEnvironments([]);
      });
    return () => {
      alive = false;
    };
  }, [client, scope.projectId]);

  // 居場所が変わったら選択は捨てる。別の場所の Environment は指せない。
  useEffect(() => setEnvironmentId(""), [scope.projectId]);
  // 添付は「次の 1 回の送信」に紐づく。送ったら空にする。
  const [attachments, setAttachments] = useState<UploadView[]>([]);
  const [restoring, setRestoring] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const subscribeFn = subscribe ?? subscribeToRun;

  const store = useMemo<Storage | null>(
    () => storage ?? (typeof sessionStorage === "undefined" ? null : sessionStorage),
    [storage],
  );

  useEffect(() => () => abortRef.current?.abort(), []);

  /** セッションの過去のやり取りを画面に流し込む。 */
  /** 実行中の Run を追い続ける。送信直後と履歴の復帰の両方から使う。 */
  const follow = useCallback(
    async (runId: string, lastEventId = 0): Promise<void> => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      await subscribeFn(client, runId, {
        signal: controller.signal,
        ...(lastEventId === 0 ? {} : { lastEventId }),
        onEvent: (event: AgentEvent) => dispatch({ type: "agent-event", event }),
        onStateChange: setConnection,
        onError: (cause: unknown) =>
          setError(cause instanceof Error ? cause.message : String(cause)),
      });
    },
    [client, subscribeFn],
  );

  const restore = useCallback(
    async (target: SessionView) => {
      setRestoring(true);
      dispatch({ type: "reset" });
      try {
        const turns = await loadSessionHistory(client, target.id);
        for (const turn of turns) {
          dispatch({
            type: "run-created",
            runId: turn.runId,
            prompt: turn.prompt,
            ts: turn.createdAt,
            keepHistory: true,
          });
          for (const event of turn.events) dispatch({ type: "agent-event", event });
          // イベントに run.result が無いこともある（cancelled など）。
          // Run の最終状態で上書きして、実行中扱いを残さない。
          dispatch({ type: "run-status-known", status: turn.status as RunStatus });
        }

        // ★ 最後の Run がまだ動いているなら、続きを追う。
        // 履歴を並べただけで購読しないと、画面がそこで止まって見える
        // （HAR で「繋ぎっぱなしの接続が 1 本も無い」ことを確認した）。
        const last = turns[turns.length - 1];
        if (last !== undefined && !TERMINAL.includes(last.status as RunStatus)) {
          const lastSeq = last.events[last.events.length - 1]?.seq ?? 0;
          void follow(last.runId, lastSeq);
        }
      } catch (cause) {
        setError(cause instanceof RcaApiError ? cause.message : String(cause));
      } finally {
        setRestoring(false);
      }
    },
    [client, follow],
  );

  // 保持されたセッションを復帰させる（タブ切り替え・リロード後）。
  useEffect(() => {
    const saved = store?.getItem(SESSION_KEY);
    if (saved === null || saved === undefined) return;

    let cancelled = false;
    void client.sessions
      .list()
      .then(async (page: Page<SessionView>) => {
        const found = page.items.find((item: SessionView) => item.id === saved);
        if (cancelled) return;
        if (found === undefined) {
          // サーバ側で消えている。保持を捨てて通常状態に戻す。
          store?.removeItem(SESSION_KEY);
          return;
        }
        setSession(found);
        await restore(found);
      })
      .catch(() => store?.removeItem(SESSION_KEY));

    return () => {
      cancelled = true;
    };
    // 初回だけ。以降の選択は明示操作で行う。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const adoptSession = useCallback(
    async (target: SessionView) => {
      setSession(target);
      store?.setItem(SESSION_KEY, target.id);
      setPicking(false);
      await restore(target);
    },
    [restore, store],
  );

  /** 走っている最中か。送信の抑止と、動かす装飾の両方がこれで決まる。 */
  const running =
    state.status === "queued" || state.status === "provisioning" || state.status === "running";

  const send = useCallback(async () => {
    const text = prompt.trim();
    if (text === "" || running) return;

    setError(null);
    setPrompt("");
    // ★ 応答を待たずに出す。detached では作成に 30 秒前後かかり、
    // 待ってから描くと入力が消えたように見える。
    dispatch({
      type: "prompt-sent",
      prompt: text,
      ts: new Date().toISOString(),
      keepHistory: session !== null,
    });
    try {
      const created = await client.runs.create({
        scope,
        prompt: text,
        ...(environmentId === "" ? {} : { environmentId }),
        ...(session === null ? {} : { sessionId: session.id }),
        // 添付があるときだけ workspace を指定する。
        // 常に送ると、セッション継続の既定（source: "session"）を壊す。
        ...(attachments.length === 0
          ? {}
          : {
              workspace: {
                source: session === null ? "empty" : "session",
                uploads: attachments.map((item) => item.uploadId),
              },
            }),
      });
      setAttachments([]);
      dispatch({
        type: "run-created",
        runId: created.runId,
        prompt: text,
        ts: new Date().toISOString(),
        // 送信時に出した仮の表示を差し替えるので、ここでは常に残す。
        keepHistory: true,
      });

      await follow(created.runId);
    } catch (cause) {
      setError(cause instanceof RcaApiError ? cause.message : String(cause));
    }
  }, [attachments, client, getToken, prompt, running, scope, session, subscribeFn]);

  const attach = useCallback(
    async (files: FileList | null) => {
      if (files === null) return;
      setError(null);
      for (const file of Array.from(files)) {
        try {
          const uploaded = await client.upload(file);
          setAttachments((current) => [...current, uploaded]);
        } catch (cause) {
          setError(cause instanceof RcaApiError ? cause.message : String(cause));
        }
      }
    },
    [client],
  );

  const cancel = useCallback(async () => {
    if (state.runId === null) return;
    try {
      await client.runs.cancel(state.runId);
    } catch (cause) {
      setError(cause instanceof RcaApiError ? cause.message : String(cause));
    }
  }, [client, state.runId]);

  const startSession = useCallback(
    async (runnerType: (typeof RUNNER_TYPES)[number]) => {
      try {
        const created = await client.sessions.create({ scope, runnerType });
        setSession(created);
        store?.setItem(SESSION_KEY, created.id);
        setPicking(false);
        dispatch({ type: "reset" });
      } catch (cause) {
        setError(cause instanceof RcaApiError ? cause.message : String(cause));
      }
    },
    [client, scope, store],
  );

  // ── 追従 ────────────────────────────────────────────────
  //
  // 新しいものが出たら下端へ寄せる。ただし**上を読んでいる間は引き戻さない**
  // （長い Run の途中で履歴を追っているときに飛ばされると読めない）。
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const stickRef = useRef(true);

  const onMessagesScroll = useCallback(() => {
    const box = messagesRef.current;
    if (box === null) return;
    // 下端から少し離れるまでは「下にいる」と見なす（端数で外れないように）。
    stickRef.current = box.scrollHeight - box.scrollTop - box.clientHeight <= 32;
  }, []);

  useEffect(() => {
    const box = messagesRef.current;
    if (box === null || !stickRef.current) return;
    box.scrollTop = box.scrollHeight - box.clientHeight;
  }, [state.messages, state.status]);

  const endSession = useCallback(async () => {
    if (session === null) return;
    await client.sessions.delete(session.id).catch(() => undefined);
    setSession(null);
    store?.removeItem(SESSION_KEY);
    dispatch({ type: "reset" });
  }, [client, session, store]);

  return (
    <div className="chat">
      <div
        className="messages"
        data-testid="messages"
        role="log"
        aria-live="polite"
        ref={messagesRef}
        onScroll={onMessagesScroll}
      >
        {state.messages.length === 0 ? (
          <p className="empty">指示を入力すると、エージェントが実行を始めます。</p>
        ) : (
          state.messages.map((message) => (
            // 新しい発言だけがふわりと現れる（既にあるものは動かない）。
            <BlurFade key={message.id}>
              <MessageRow message={message} />
            </BlurFade>
          ))
        )}
        {state.unknownEvents > 0 && <RawEventLog state={state} />}
      </div>

      {/* 終わったその場で成果物を取れるようにする。
          Runs の詳細まで行かないと落とせないのは遠い。 */}
      {state.runId !== null && TERMINAL.includes(state.status as RunStatus) && (
        <>
          <RunArtifacts client={client} runId={state.runId} />
          <RunWorkspace client={client} runId={state.runId} />
        </>
      )}

      <div className="stack">
        {error !== null && (
          <p className="error-banner" role="alert">
            {error}
          </p>
        )}

        <div className="row">
          {/*
            ★ 走っている間だけ、状態表示の枠を光が回る。
            detached では 30 秒級の無反応があり、止まっているのか
            動いているのかが文字だけでは分からない。
          */}
          <span
            className={`badge ${STATUS_TONE[state.status] ?? ""}${running ? " running" : ""}`}
            data-testid="run-status"
          >
            {running && <BorderBeam size={36} duration={2.5} radius="999px" />}
            {state.status}
          </span>
          {connection !== "idle" && (
            <span className="muted mono" data-testid="connection">
              接続: {connection}
            </span>
          )}
          {state.usage !== null && (
            <span className="muted mono" data-testid="usage">
              in {state.usage.inputTokens} / out {state.usage.outputTokens} / $
              {state.usage.costUsd.toFixed(4)}
            </span>
          )}
          <span className="spacer" />
          {/* ★ 走らせる前に選ぶ。既定は「自動」＝これまでどおりスコープチェーン。 */}
          {environments.length > 0 && (
            <>
              <label htmlFor="chat-environment" className="muted">
                Environment
              </label>
              <select
                id="chat-environment"
                value={environmentId}
                onChange={(e) => setEnvironmentId(e.target.value)}
              >
                <option value="">（自動）</option>
                {environments.map((doc) => (
                  <option key={doc.id} value={doc.id}>
                    {doc.name}
                  </option>
                ))}
              </select>
            </>
          )}
          {restoring && <ShinyText>履歴を読み込み中…</ShinyText>}
          {session === null ? (
            <button type="button" onClick={() => setPicking((v) => !v)}>
              セッションを選ぶ
            </button>
          ) : (
            <>
              <span className="badge" data-testid="session">
                セッション {session.id}（{session.runnerType}）
              </span>
              <button type="button" onClick={() => void endSession()}>
                終了
              </button>
            </>
          )}
        </div>

        {picking && (
          <SessionPicker
            client={client}
            onResume={(target) => void adoptSession(target)}
            onCreate={(runnerType) => void startSession(runnerType)}
            onError={setError}
          />
        )}

        {attachments.length > 0 && (
          <div className="attachments">
            {attachments.map((item) => (
              <span key={item.uploadId} className="badge">
                {item.name}
                <button
                  type="button"
                  aria-label={`${item.name} を外す`}
                  className="attachment-remove"
                  onClick={() =>
                    setAttachments((current) =>
                      current.filter((other) => other.uploadId !== item.uploadId),
                    )
                  }
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        <form
          className="composer"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <textarea
            aria-label="指示"
            placeholder="例: src/ のテストを全て通るようにして"
            value={prompt}
            disabled={running}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <label className="attach-button">
            <input
              type="file"
              multiple
              aria-label="ファイルを添付"
              style={{ display: "none" }}
              disabled={running}
              onChange={(e) => {
                void attach(e.target.files);
                // 同じファイルを続けて選べるようにする。
                e.target.value = "";
              }}
            />
            添付
          </label>
          {running ? (
            <button type="button" className="danger" onClick={() => void cancel()}>
              キャンセル
            </button>
          ) : (
            <button type="submit" className="primary" disabled={prompt.trim() === ""}>
              送信
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

/**
 * ツールに渡した内容を 1 つの文字列にする。
 *
 * Bash の `command` のように「これを実行した」と読める鍵があればそれを優先し、
 * 無ければ丸ごと出す。空の入力は行を作らない。
 */
export function toolInputText(input: unknown): string | null {
  if (input === undefined || input === null) return null;
  if (typeof input === "string") return input === "" ? null : input;
  if (typeof input !== "object") return String(input);

  const record = input as Record<string, unknown>;
  if (Object.keys(record).length === 0) return null;

  for (const key of ["command", "file_path", "path", "pattern", "url", "query"]) {
    const value = record[key];
    if (typeof value === "string" && value !== "") return value;
  }
  return JSON.stringify(record, null, 2);
}

/** 終わった Run の成果物を並べ、認証付きで取らせる。 */
function RunArtifacts({ client, runId }: { client: RcaClient; runId: string }): React.JSX.Element {
  const [items, setItems] = useState<readonly { name: string }[]>([]);

  useEffect(() => {
    let alive = true;
    client.runs
      .artifacts(runId)
      .then((res) => {
        // 一覧が取れないことはある（権限や 404）。その場合は何も出さない。
        if (alive) setItems(res?.artifacts ?? []);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [client, runId]);

  if (items.length === 0) return <></>;
  return (
    <div className="row" data-testid="chat-artifacts">
      <span className="muted">成果物:</span>
      {items.map((artifact) => (
        <button
          key={artifact.name}
          type="button"
          className="linklike"
          onClick={() => void downloadArtifact(client, runId, artifact.name)}
        >
          {artifact.name}
        </button>
      ))}
    </div>
  );
}

/**
 * ワークスペースに残っているファイルを並べ、認証付きで取らせる。
 *
 * 成果物（diff と一覧）とは別で、**エージェントが `out/` に置いたファイル**はこちら。
 * 秘密が混ざり得るので保存はしていない（AC-11）。そのため取れるのは
 * コンテナが生きている間だけで、消えた後は空になる。
 */
function RunWorkspace({ client, runId }: { client: RcaClient; runId: string }): React.JSX.Element {
  const [items, setItems] = useState<readonly { path: string }[]>([]);

  useEffect(() => {
    let alive = true;
    client.runs
      .workspace(runId)
      .then((res) => {
        if (alive) setItems(res?.files ?? []);
      })
      // コンテナが解放済みなら 409。出さないだけでよい。
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [client, runId]);

  if (items.length === 0) return <></>;
  return (
    <div className="row" data-testid="chat-workspace">
      <span className="muted">成果物ファイル（out/）:</span>
      {items.map((file) => (
        <button
          key={file.path}
          type="button"
          className="linklike"
          onClick={() => void downloadWorkspaceFile(client, runId, file.path)}
        >
          {file.path}
        </button>
      ))}
    </div>
  );
}

/** ワークスペースのファイルを認証付きで取り、ブラウザに保存させる。 */
async function downloadWorkspaceFile(
  client: RcaClient,
  runId: string,
  path: string,
): Promise<void> {
  const response = await client.runs.workspaceFile(runId, path);
  await saveResponse(response, path.slice(path.lastIndexOf("/") + 1));
}

/**
 * 成果物を認証付きで取り、ブラウザに保存させる。
 *
 * API は Bearer を要求するので `<a href>` では落ちる（本番で 401 を確認）。
 */
async function downloadArtifact(client: RcaClient, runId: string, name: string): Promise<void> {
  await saveResponse(await client.runs.artifact(runId, name), name);
}

/** 受け取った本文をその名前で保存させる。 */
async function saveResponse(response: Response, name: string): Promise<void> {
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = name;
    anchor.click();
  } finally {
    URL.revokeObjectURL(url);
  }
}

function MessageRow({ message }: { message: ChatMessage }): React.JSX.Element {
  if (message.kind === "tool") {
    const tone =
      message.tool?.status === "ok" ? "ok" : message.tool?.status === "failed" ? "danger" : "warn";
    return (
      <div className="message" data-testid="message-tool">
        <div className="who">
          ツール <span className={`badge ${tone}`}>{message.tool?.status}</span>
        </div>
        <strong className="mono">{message.text}</strong>
        {/* 何を実行したかを出す。結果だけでは追えない。 */}
        {toolInputText(message.tool?.input) !== null && (
          <pre className="mono tool-input">{toolInputText(message.tool?.input)}</pre>
        )}
        {message.tool?.output !== undefined && (
          <pre className="mono">{format(message.tool.output)}</pre>
        )}
      </div>
    );
  }

  const label: Record<ChatMessage["kind"], string> = {
    prompt: "あなた",
    assistant: "エージェント",
    thinking: "思考",
    error: "エラー",
    status: "",
    tool: "ツール",
  };

  return (
    <div className={`message ${message.kind}`} data-testid={`message-${message.kind}`}>
      {label[message.kind] !== "" && <div className="who">{label[message.kind]}</div>}
      <div style={{ whiteSpace: "pre-wrap" }}>{message.text}</div>
    </div>
  );
}

/**
 * 既存セッションの選択と新規作成（FR-3.1 / FR-3.2）。
 *
 * セッション ID を手で入力させない。再開したいものを一覧から選ぶ。
 */
function SessionPicker({
  client,
  onResume,
  onCreate,
  onError,
}: {
  client: RcaClient;
  onResume: (session: SessionView) => void;
  onCreate: (runnerType: (typeof RUNNER_TYPES)[number]) => void;
  onError: (message: string) => void;
}): React.JSX.Element {
  const [sessions, setSessions] = useState<SessionView[] | null>(null);
  const [selected, setSelected] = useState("");
  const [runnerType, setRunnerType] = useState<(typeof RUNNER_TYPES)[number]>(RUNNER_TYPES[0]);

  useEffect(() => {
    client.sessions
      .list()
      .then((page: Page<SessionView>) => {
        const active = page.items.filter((item: SessionView) => item.status !== "deleted");
        setSessions(active);
        setSelected(active[0]?.id ?? "");
      })
      .catch((cause: unknown) => {
        setSessions([]);
        onError(cause instanceof RcaApiError ? cause.message : String(cause));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  return (
    <div className="card stack">
      <div className="filters">
        <label htmlFor="chat-session">セッション</label>
        <select
          id="chat-session"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          disabled={sessions === null || sessions.length === 0}
        >
          {(sessions ?? []).map((item) => (
            <option key={item.id} value={item.id}>
              {item.id}（{item.runnerType} / 最終 {new Date(item.lastActiveAt).toLocaleString()}）
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={selected === ""}
          onClick={() => {
            const target = (sessions ?? []).find((item) => item.id === selected);
            if (target !== undefined) onResume(target);
          }}
        >
          再開
        </button>
      </div>

      {sessions !== null && sessions.length === 0 && (
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          再開できるセッションはありません。新しく作ってください。
        </p>
      )}

      <div className="filters">
        <label htmlFor="chat-runner">Runner</label>
        <select
          id="chat-runner"
          value={runnerType}
          onChange={(e) => setRunnerType(e.target.value as (typeof RUNNER_TYPES)[number])}
        >
          {RUNNER_TYPES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        <button type="button" className="primary" onClick={() => onCreate(runnerType)}>
          新規作成
        </button>
      </div>
    </div>
  );
}

/**
 * 構造化できなかった CLI 出力（`runner.raw`）。
 *
 * 会話としては意味を成さないので既定では畳むが、**開けるようにしておく**。
 * 認証エラーやツールの拒否理由はここにしか出ないことが多く、
 * 件数だけ知らされても何も分からない。
 */
function RawEventLog({ state }: { state: ChatState }): React.JSX.Element {
  const dropped = state.unknownEvents - state.rawEvents.length;

  return (
    <details className="raw-log">
      <summary className="muted mono">
        エージェントの生ログ {state.unknownEvents} 件
        {dropped > 0 ? `（直近 ${state.rawEvents.length} 件を表示）` : ""}
      </summary>
      <div className="stack">
        {state.rawEvents.map((entry) => (
          <pre key={entry.seq} className="mono raw-entry">
            {entry.note === undefined ? "" : `${entry.note}\n`}
            {format(entry.raw)}
          </pre>
        ))}
      </div>
    </details>
  );
}

function format(value: unknown): string {
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
